'use client';

import React, { useMemo, useState } from 'react';
import { ArrowUpDown, Layers, Smartphone, MonitorSmartphone, Globe2, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
    DeviceTypeIcon,
    getDeviceTypeLabel,
    OSIcon,
    getOSLabel,
    BrowserIcon,
    getBrowserLabel,
    isInAppBrowser,
    UTMSourceIcon,
    getUTMSourceLabel,
} from '@/components/ui/device-icons';

// ============================================================
// PUBLIC DATA SHAPE (mirrors the planned tRPC payload 1:1, so the
// backend wiring later only swaps the data source — no UI changes)
// ============================================================

export type BreakdownDimension = 'source' | 'device' | 'os' | 'browser' | 'country';

export interface BreakdownDatum {
    /** Raw value as stored (e.g. 'iOS', 'FR', 'facebook', 'mobile'). */
    value: string;
    /** Total funnel sessions for this value. */
    sessions: number;
    /** Sessions that reached the funnel's conversion (final) step. */
    conversions: number;
    /** Optional UTM medium, only for the 'source' dimension. */
    medium?: string;
}

export type FunnelBreakdownData = Partial<Record<BreakdownDimension, BreakdownDatum[]>>;

/** Conversion goal the breakdown is measured against. */
export type BreakdownScope = 'funnel' | 'step';

interface FunnelBreakdownPanelProps {
    data: FunnelBreakdownData | undefined;
    isLoading: boolean;
    /** Conversion rate (%) of the current goal, used as the comparison baseline. */
    overallConversionRate: number;
    /** Name of the conversion goal step (shown in the metric definition). */
    conversionStepName?: string;
    /** Whether the breakdown is scoped to the whole funnel or a selected step. */
    scope: BreakdownScope;
    onScopeChange: (scope: BreakdownScope) => void;
    /** True when a meaningful step is selected on the canvas (enables 'step' scope). */
    stepScopeAvailable: boolean;
    /** Name of the selected step, used as the 'step' toggle label. */
    stepScopeName?: string;
    /** Top divider — drop it when the panel sits in its own column. Defaults to true. */
    bordered?: boolean;
}

type SortMode = 'conversion' | 'volume';

const DIMENSIONS: { id: BreakdownDimension; labelKey: string; icon: React.ReactNode }[] = [
    { id: 'source', labelKey: 'analytics.breakdown.dimSource', icon: <Target className="h-3.5 w-3.5" /> },
    { id: 'device', labelKey: 'analytics.breakdown.dimDevice', icon: <Smartphone className="h-3.5 w-3.5" /> },
    { id: 'os', labelKey: 'analytics.breakdown.dimOS', icon: <MonitorSmartphone className="h-3.5 w-3.5" /> },
    { id: 'browser', labelKey: 'analytics.breakdown.dimBrowser', icon: <Globe2 className="h-3.5 w-3.5" /> },
    { id: 'country', labelKey: 'analytics.breakdown.dimCountry', icon: <Globe2 className="h-3.5 w-3.5" /> },
];

// ============================================================
// COUNTRY HELPERS (no flag helper exists in device-icons)
// ============================================================

const COUNTRY_NAMES: Record<string, string> = {
    FR: 'France', MA: 'Morocco', BE: 'Belgium', US: 'United States', CA: 'Canada',
    IT: 'Italy', AE: 'UAE', GY: 'Guyana', CH: 'Switzerland', IL: 'Israel',
    RE: 'Réunion', DE: 'Germany', GP: 'Guadeloupe', TN: 'Tunisia', MQ: 'Martinique',
    ES: 'Spain', GB: 'United Kingdom', NL: 'Netherlands', PT: 'Portugal', LU: 'Luxembourg',
    DZ: 'Algeria', SN: 'Senegal', CI: "Côte d'Ivoire", AU: 'Australia',
};

/** Turn an ISO 3166-1 alpha-2 code into its flag emoji. */
function countryFlag(code: string): string {
    const cc = code?.toUpperCase();
    if (!cc || cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return '🌐';
    const A = 0x1f1e6;
    return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65);
}

// ============================================================
// PER-DIMENSION ICON + LABEL RESOLUTION
// ============================================================

function resolveRow(dimension: BreakdownDimension, datum: BreakdownDatum): {
    label: string;
    icon: React.ReactNode;
    isInApp?: boolean;
} {
    const isUnknown = !datum.value || datum.value === '(unknown)' || datum.value === '(direct)';
    switch (dimension) {
        case 'device':
            return {
                label: getDeviceTypeLabel(datum.value),
                icon: <DeviceTypeIcon deviceType={datum.value} className="h-4 w-4 text-muted-foreground" />,
            };
        case 'os':
            return {
                label: getOSLabel(datum.value),
                icon: <OSIcon os={datum.value} className="h-4 w-4" />,
            };
        case 'browser':
            return {
                label: getBrowserLabel(datum.value),
                icon: <BrowserIcon browser={datum.value} className="h-4 w-4" />,
                isInApp: isInAppBrowser(datum.value),
            };
        case 'country':
            return {
                label: isUnknown ? 'Unknown' : (COUNTRY_NAMES[datum.value?.toUpperCase()] || datum.value.toUpperCase()),
                icon: <span className="text-base leading-none">{isUnknown ? '🌐' : countryFlag(datum.value)}</span>,
            };
        case 'source':
        default:
            return {
                label: getUTMSourceLabel(datum.value, datum.medium),
                icon: <UTMSourceIcon source={datum.value} medium={datum.medium} className="h-4 w-4" />,
            };
    }
}

// ============================================================
// COMPONENT
// ============================================================

export function FunnelBreakdownPanel({
    data,
    isLoading,
    overallConversionRate,
    conversionStepName,
    scope,
    onScopeChange,
    stepScopeAvailable,
    stepScopeName,
    bordered = true,
}: FunnelBreakdownPanelProps) {
    const { t } = useTranslation('storefront');
    const [dimension, setDimension] = useState<BreakdownDimension>('source');
    const [sortMode, setSortMode] = useState<SortMode>('conversion');

    const rows = data?.[dimension] ?? [];
    const totalSessions = useMemo(() => rows.reduce((s, r) => s + r.sessions, 0), [rows]);

    // A segment needs enough volume before its rate is trustworthy.
    const minVolume = useMemo(() => Math.max(20, Math.round(totalSessions * 0.02)), [totalSessions]);

    const prepared = useMemo(() => {
        const enriched = rows.map((r) => {
            const rate = r.sessions > 0 ? (r.conversions / r.sessions) * 100 : 0;
            const share = totalSessions > 0 ? (r.sessions / totalSessions) * 100 : 0;
            return { ...r, rate, share, lowVolume: r.sessions < minVolume };
        });

        enriched.sort((a, b) => {
            if (sortMode === 'volume') return b.sessions - a.sessions;
            // Conversion sort: trustworthy segments first, then by rate, then by volume.
            if (a.lowVolume !== b.lowVolume) return a.lowVolume ? 1 : -1;
            if (b.rate !== a.rate) return b.rate - a.rate;
            return b.sessions - a.sessions;
        });
        return enriched;
    }, [rows, totalSessions, minVolume, sortMode]);

    // Bar scale: spread by the best rate so differences are legible.
    const maxRate = useMemo(
        () => Math.max(overallConversionRate, ...prepared.map((r) => r.rate), 1),
        [prepared, overallConversionRate],
    );
    const avgMarkerLeft = Math.min(100, (overallConversionRate / maxRate) * 100);

    // The single best significant segment (answers "which converts best?").
    const bestValue = useMemo(() => {
        const significant = prepared.filter((r) => !r.lowVolume);
        if (significant.length < 2) return null;
        const best = significant.reduce((a, b) => (b.rate > a.rate ? b : a));
        // Only flag it if it actually beats the average.
        return best.rate > overallConversionRate ? best.value : null;
    }, [prepared, overallConversionRate]);

    return (
        <div className={cn('px-4 py-4', bordered && 'border-t')}>
            {/* Header */}
            <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                        {t('analytics.breakdown.title')}
                    </h3>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {conversionStepName
                            ? t('analytics.breakdown.conversionDefNamed', { step: conversionStepName })
                            : t('analytics.breakdown.conversionDef')}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setSortMode((m) => (m === 'conversion' ? 'volume' : 'conversion'))}
                    className="flex flex-shrink-0 items-center gap-1 rounded-md border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    title={t('analytics.breakdown.sortToggleHint')}
                >
                    <ArrowUpDown className="h-3 w-3" />
                    {sortMode === 'conversion'
                        ? t('analytics.breakdown.sortByConversion')
                        : t('analytics.breakdown.sortByVolume')}
                </button>
            </div>

            {/* Scope toggle — only when a step is selected on the canvas */}
            {stepScopeAvailable && (
                <div className="mb-3 inline-flex rounded-md border bg-muted/40 p-0.5 text-[11px] font-medium">
                    <button
                        type="button"
                        onClick={() => onScopeChange('funnel')}
                        className={cn(
                            'rounded px-2 py-0.5 transition-colors',
                            scope === 'funnel'
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {t('analytics.breakdown.scopeFunnel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => onScopeChange('step')}
                        className={cn(
                            'flex items-center gap-1 rounded px-2 py-0.5 transition-colors',
                            scope === 'step'
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {stepScopeName
                            ? t('analytics.breakdown.scopeStepNamed', { step: stepScopeName })
                            : t('analytics.breakdown.scopeStep')}
                    </button>
                </div>
            )}

            {/* Dimension switcher */}
            <div className="mb-3 -mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
                {DIMENSIONS.map((d) => {
                    const active = d.id === dimension;
                    return (
                        <button
                            key={d.id}
                            type="button"
                            onClick={() => setDimension(d.id)}
                            className={cn(
                                'flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                                active
                                    ? 'bg-foreground text-background'
                                    : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                            )}
                        >
                            {d.icon}
                            {t(d.labelKey)}
                        </button>
                    );
                })}
            </div>

            {/* Body */}
            {isLoading ? (
                <div className="space-y-2.5">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-9 w-full rounded-md" />
                    ))}
                </div>
            ) : prepared.length === 0 ? (
                <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">
                    {t('analytics.breakdown.noData')}
                </div>
            ) : (
                <>
                    {/* Average reference legend */}
                    <div className="mb-2 flex items-center justify-between text-[10px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block h-3 w-px bg-foreground/40" />
                            {t('analytics.breakdown.funnelAverage', { rate: overallConversionRate.toFixed(1) })}
                        </span>
                        <span>{t('analytics.breakdown.sessionsHeader')}</span>
                    </div>

                    <div className="relative space-y-1.5">
                        {/* Shared average reference line spanning the bar column */}
                        {prepared.length > 0 && (
                            <div
                                aria-hidden
                                className="pointer-events-none absolute bottom-1 top-1 z-10 w-px bg-foreground/30"
                                style={{ left: `calc(${avgMarkerLeft}% )` }}
                            />
                        )}
                        {prepared.map((row) => {
                            const { label, icon, isInApp } = resolveRow(dimension, row);
                            const barWidth = Math.max(2, (row.rate / maxRate) * 100);
                            const above = row.rate >= overallConversionRate;
                            const isBest = row.value === bestValue;

                            return (
                                <div
                                    key={`${dimension}-${row.value}`}
                                    className={cn(
                                        'group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/40',
                                        row.lowVolume && 'opacity-55',
                                    )}
                                >
                                    {/* Left: label + bar */}
                                    <div className="min-w-0">
                                        <div className="mb-1 flex items-center gap-1.5">
                                            <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                                                {icon}
                                            </span>
                                            <span className="truncate text-xs font-medium text-foreground">{label}</span>
                                            {isInApp && (
                                                <span
                                                    className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500"
                                                    title={t('analytics.breakdown.inAppHint')}
                                                />
                                            )}
                                            {isBest && (
                                                <span className="flex-shrink-0 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                                                    {t('analytics.breakdown.best')}
                                                </span>
                                            )}
                                        </div>
                                        {/* Conversion-rate bar */}
                                        <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
                                            <div
                                                className={cn(
                                                    'absolute inset-y-0 left-0 rounded-full transition-[width] duration-500',
                                                    row.lowVolume
                                                        ? 'bg-muted-foreground/40'
                                                        : above
                                                            ? 'bg-emerald-500'
                                                            : row.rate >= overallConversionRate * 0.5
                                                                ? 'bg-amber-500'
                                                                : 'bg-rose-400',
                                                )}
                                                style={{ width: `${barWidth}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Right: rate + volume */}
                                    <div className="flex flex-col items-end justify-center">
                                        <span
                                            className={cn(
                                                'text-sm font-bold tabular-nums leading-none',
                                                row.lowVolume
                                                    ? 'text-muted-foreground'
                                                    : above
                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                        : 'text-foreground',
                                            )}
                                        >
                                            {row.rate.toFixed(1)}%
                                        </span>
                                        <span className="mt-0.5 text-[10px] tabular-nums text-muted-foreground whitespace-nowrap">
                                            {row.sessions.toLocaleString()} · {row.share.toFixed(0)}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
